import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';

export interface ParticipantReportDto {
  userId: string;
  userName: string;
  userEmail: string;
  teamId?: string;
  teamName?: string;
  attempts: QuizAttemptHistoryDto[];
  totalAttempts: number;
  completedAttempts: number;
  passedAttempts: number;
  failedAttempts: number;
  averageScore?: number;
  bestScore?: number;
  hasPassedAllQuizzes: boolean;
  totalQuizzesInClass: number;
  passedQuizzesCount: number;
}

export interface QuizAttemptHistoryDto {
  attemptId: string;
  quizId: string;
  quizTitle: string;
  startedAt: string;
  completedAt?: string;
  timeSpentSeconds?: number;
  score?: number;
  isPassed?: boolean;
  status: number;
  attemptNumber: number;
}

export interface ParticipantComparisonDto {
  userId: string;
  userName: string;
  teamId?: string;
  teamName?: string;
  averageScore?: number;
  totalAttempts: number;
  passedAttempts: number;
  failedAttempts: number;
  approvalRate: number;
  totalTimeSpentSeconds?: number;
  rank: number;
  quizScores: Record<string, number | null>;
}

export interface TeamComparisonDto {
  teamId: string;
  teamName: string;
  startDate: string;
  endDate?: string;
  totalTrainees: number;
  activeTrainees: number;
  traineesPassed: number;
  traineesFailed: number;
  approvalRate: number;
  averageScore: number;
  medianScore?: number;
  standardDeviation?: number;
  totalQuizzes: number;
  totalAttempts: number;
  quizMetrics: Record<string, TeamQuizMetricsDto>;
}

export interface TeamQuizMetricsDto {
  quizId: string;
  quizTitle: string;
  totalAttempts: number;
  passedAttempts: number;
  failedAttempts: number;
  approvalRate: number;
  averageScore: number;
}

export interface TopPerformersDto {
  userId: string;
  userName: string;
  userEmail: string;
  teamId?: string;
  teamName?: string;
  averageScore: number;
  bestScore?: number;
  totalQuizzesPassed: number;
  totalAttempts: number;
  rank: number;
  quizScores: Record<string, number | null>;
}

export interface TemporalAnalysisDto {
  periodStart: string;
  periodEnd: string;
  dataPoints: TemporalDataPointDto[];
  teamsInPeriod: TeamComparisonDto[];
  overallAverageScore: number;
  overallApprovalRate: number;
  totalTrainees: number;
  totalPassed: number;
}

export interface TemporalDataPointDto {
  date: string;
  traineesCount: number;
  averageScore: number;
  approvalRate: number;
  completedAttempts: number;
}

export interface TrainingMetricsDto {
  totalTrainingTeams: number;
  activeTrainingTeams: number;
  totalTrainees: number;
  activeTrainees: number;
  traineesPassed: number;
  traineesFailed: number;
  overallApprovalRate: number;
  overallAverageScore: number;
  totalQuizzes: number;
  totalAttempts: number;
  completedAttempts: number;
  inProgressAttempts: number;
}

export class QuizReportService extends BaseApiService {
  async getParticipantReport(userId: string, teamId?: string): Promise<ParticipantReportDto> {
    const params = teamId ? { teamId } : {};
    const response = await this.api.get<ApiResponse<ParticipantReportDto>>(`/reports/participant/${userId}`, { params });
    return this.extractData(response);
  }

  async getParticipantsComparison(teamId?: string, quizId?: string): Promise<ParticipantComparisonDto[]> {
    const params: any = {};
    if (teamId) params.teamId = teamId;
    if (quizId) params.quizId = quizId;
    const response = await this.api.get<ApiResponse<ParticipantComparisonDto[]>>('/reports/participants/comparison', { params });
    return this.extractData(response);
  }

  async getTeamsComparison(startDate?: string, endDate?: string): Promise<TeamComparisonDto[]> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.get<ApiResponse<TeamComparisonDto[]>>('/reports/teams/comparison', { params });
    return this.extractData(response);
  }

  async getTopPerformers(teamId?: string, quizId?: string, startDate?: string, endDate?: string, limit?: number): Promise<TopPerformersDto[]> {
    const params: any = {};
    if (teamId) params.teamId = teamId;
    if (quizId) params.quizId = quizId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (limit) params.limit = limit;
    const response = await this.api.get<ApiResponse<TopPerformersDto[]>>('/reports/top-performers', { params });
    return this.extractData(response);
  }

  async getTemporalAnalysis(startDate: string, endDate: string): Promise<TemporalAnalysisDto> {
    const response = await this.api.get<ApiResponse<TemporalAnalysisDto>>('/reports/temporal-analysis', {
      params: { startDate, endDate }
    });
    return this.extractData(response);
  }

  async getTrainingMetrics(): Promise<TrainingMetricsDto> {
    const response = await this.api.get<ApiResponse<TrainingMetricsDto>>('/reports/training-metrics');
    return this.extractData(response);
  }
}

export const quizReportService = new QuizReportService();
export default quizReportService;
